import { payInAdvance } from "../api/paymentsAPI";

/**
 * Make a Payment (pay toward the account balance), including the card's
 * extra verification step (3-D Secure).
 *
 * When a card needs the bank's verification, the server answers
 * `requires_action` with the payment's client secret instead of charging.
 * The browser must then run Stripe's own confirm (which shows the bank's
 * challenge) and tell the server to record the verified payment. The Make a
 * Payment window on the billing page skipped that step: it treated
 * `requires_action` as a failure, so a card that needs verification could
 * never pay there. Both Make a Payment screens use this one function.
 *
 * `form.idempotencyKey` identifies ONE payment attempt (utils/attemptKey):
 * the server uses it so a retry or a double click is one charge.
 * `form.membershipId` is the account being paid toward (the one shown in the
 * family switcher); the server credits it only after checking the signed-in
 * person is on that account.
 */
export interface PayTowardBalanceForm {
  amount: number;
  paymentMethodId: string;
  paymentType: string;
  saveCard?: boolean;
  membershipId?: string | null;
  idempotencyKey?: string | null;
  processed_by?: unknown;
}

interface StripeLike {
  confirmCardPayment: (clientSecret: string) => Promise<{
    error?: { message?: string } | null;
    paymentIntent?: { id: string; status: string; payment_method?: unknown } | null;
  }>;
}

export async function payTowardBalance({
  token,
  stripe,
  form,
  onVerifying,
}: {
  token: string | null;
  stripe: StripeLike | null | undefined;
  form: PayTowardBalanceForm;
  onVerifying?: () => void;
}): Promise<any> {
  const res = await payInAdvance(token, form);
  if (!res?.requires_action || !res?.payment_intent_client_secret) return res;

  if (!stripe) {
    return {
      success: false,
      answered: true,
      error: "Your card needs to be verified, but the card form is not ready. Please try again.",
    };
  }
  onVerifying?.();
  const { error, paymentIntent } = await stripe.confirmCardPayment(res.payment_intent_client_secret);
  if (error) {
    return { success: false, answered: true, error: `Card verification failed: ${error.message || "Please try again."}` };
  }
  if (paymentIntent?.status !== "succeeded") {
    return { success: false, answered: true, error: "Payment could not be completed after verification." };
  }
  // Verified: the server checks this payment with the card processor and
  // records it (it does not charge again).
  return payInAdvance(token, {
    ...form,
    paymentMethodId: (paymentIntent.payment_method as string) || form.paymentMethodId,
    paymentIntentId: paymentIntent.id,
  });
}
